import { Skeleton } from '@/components/ui/skeleton';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';
import { PendingRegion } from './PendingRegion';

/**
 * A list's placeholder, shaped like its rows (spec 126 FR-011).
 *
 * Mounted only while the read is pending. Nothing is drawn for the first
 * moment — a fast read never flashes a placeholder — and once drawn it stays
 * long enough to be read as one (`usePendingDisplay`). Replaces the bare
 * "Loading…" lines that blinked on every drawer tab.
 */
export function PendingRows({
  label,
  rows = 3,
  testId,
}: {
  /** What is loading, for assistive technology: "Loading comments". */
  label: string;
  rows?: number;
  testId?: string;
}) {
  const visible = usePendingDisplay(true);
  if (!visible) return null;
  return <SkeletonRows label={label} rows={rows} testId={testId} />;
}

/**
 * The same rows without the delay, for a caller that already times its
 * placeholder with `usePendingDisplay`.
 */
export function SkeletonRows({
  label,
  rows = 3,
  testId,
}: {
  label: string;
  rows?: number;
  testId?: string;
}) {
  return (
    <PendingRegion label={label} className="gap-2" data-testid={testId}>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} aria-hidden="true" className="flex flex-col gap-1.5">
          <Skeleton className="h-3.5 w-2/5" />
          <Skeleton className="h-3 w-4/5" />
        </div>
      ))}
    </PendingRegion>
  );
}
